import { Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * Reglas Si / No / Entonces de una función de tarea.
 * Lee ui_sections del catálogo (core + if_yes + if_no).
 */
export default function TaskFunctionLogic({
  selectedFn,
  functionConfig,
  onConfigChange,
  waTemplates = [],
  disabled = false,
}) {
  if (!selectedFn) return null;

  if (selectedFn.locked) {
    return (
      <p className="text-[11px] text-amber-700 flex items-center gap-1 mt-1 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
        <Lock className="w-3 h-3 shrink-0" /> {selectedFn.lock_reason}
      </p>
    );
  }

  const sections = selectedFn.ui_sections || [];
  const cfg = functionConfig || {};

  const setField = (key, value) => {
    onConfigChange({ ...cfg, [key]: value });
  };

  const renderField = (field) => {
    const val = cfg[field.key] ?? field.default ?? '';
    if (field.type === 'readonly') {
      return (
        <p className="text-[11px] text-[#334155] bg-white border border-dashed border-[#E2E8F0] rounded px-2 py-1.5">
          {field.default || field.label}
        </p>
      );
    }
    if (field.type === 'textarea') {
      return (
        <textarea
          className="w-full min-h-[64px] text-xs border border-[#E2E8F0] rounded-md px-2 py-1.5"
          value={val}
          disabled={disabled}
          placeholder={field.label}
          onChange={(e) => setField(field.key, e.target.value)}
          data-testid={`task-fn-field-${field.key}`}
        />
      );
    }
    if (field.type === 'boolean') {
      return (
        <label className="flex items-center gap-2 text-xs text-[#334155]">
          <input
            type="checkbox"
            checked={val !== false && val !== 'false' && val !== 0}
            disabled={disabled}
            onChange={(e) => setField(field.key, e.target.checked)}
          />
          {field.label}
        </label>
      );
    }
    if (field.type === 'wa_template') {
      return (
        <Select
          value={val || 'none'}
          onValueChange={(v) => setField(field.key, v === 'none' ? '' : v)}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 text-xs" data-testid={`task-fn-field-${field.key}`}>
            <SelectValue placeholder="Elegir plantilla…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sin template</SelectItem>
            {waTemplates.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        className="h-8 text-xs"
        value={val}
        disabled={disabled}
        placeholder={field.label}
        onChange={(e) => setField(field.key, e.target.value)}
        data-testid={`task-fn-field-${field.key}`}
      />
    );
  };

  const sectionStyle = (id) => {
    if (id === 'if_yes') return 'border-emerald-200 bg-emerald-50/60';
    if (id === 'if_no') return 'border-amber-200 bg-amber-50/50';
    return 'border-[#E2E8F0] bg-white';
  };

  const sectionBadge = (id) => {
    if (id === 'if_yes') return { text: 'SI', cls: 'bg-emerald-600 text-white' };
    if (id === 'if_no') return { text: 'NO', cls: 'bg-amber-600 text-white' };
    return null;
  };

  return (
    <div className="space-y-2 mt-2" data-testid="task-function-logic">
      {selectedFn.logic_question && (
        <p className="text-[11px] text-[#475569]">
          <span className="font-semibold text-[#0F172A]">Regla: </span>
          {selectedFn.logic_question}
        </p>
      )}
      <p className="text-[10px] text-[#94A3B8]">{selectedFn.description}</p>

      {sections.map((sec) => {
        const badge = sectionBadge(sec.id);
        return (
          <div
            key={sec.id}
            className={`rounded-lg border p-2.5 space-y-2 ${sectionStyle(sec.id)}`}
            data-testid={`task-fn-section-${sec.id}`}
          >
            <div className="flex items-center gap-2">
              {badge && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.text}</span>
              )}
              <p className="text-[11px] font-semibold text-[#0F172A]">{sec.title}</p>
              {(sec.id === 'if_yes' || sec.id === 'if_no') && (
                <span className="text-[9px] text-[#64748B] font-medium">→ Entonces</span>
              )}
            </div>
            {(sec.fields || []).map((field) => (
              <div key={field.key}>
                {field.type !== 'boolean' && field.type !== 'readonly' && (
                  <label className="text-[10px] font-medium text-[#475569] block mb-0.5">
                    {field.label}
                    {field.required ? ' *' : ''}
                  </label>
                )}
                {renderField(field)}
                {field.help && (
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">{field.help}</p>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** Resumen legible de la config guardada (lista / ficha). */
export function summarizeFunctionConfig(fn, config) {
  if (!fn || !config) return null;
  const parts = [];
  if (config.objective) parts.push(`Objetivo: ${config.objective}`);
  if (config.ai_message) parts.push('Guion IA definido');
  if (config.spoken_message) parts.push('Mensaje corto definido');
  if (config.wa_template_id || config.on_success_wa_template_id) parts.push('Template SI');
  if (config.on_fail_wa_template_id) parts.push('Template NO');
  if (config.on_reschedule_wa_template_id) parts.push('Template reagendar');
  return parts.length ? parts.join(' · ') : null;
}
